import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ShieldCheck } from 'lucide-react';
import { getApiBaseUrl } from '../services/api';

/**
 * Connect Claude — the identity step of the Agents Anywhere connector.
 *
 * Consultants live here, so the builder sends them to this page to sign in
 * and approve (CTO 2026-08-01). We never see or hand out a key: on Allow the
 * backend signs an identity assertion with the shared provisioning secret and
 * returns the builder URL that completes the OAuth grant, which sends the
 * consultant straight back to Claude.
 *
 * Query: ?req=<builder's signed request>&return=<builder callback>&fallback=<builder-login URL>
 */
const ConnectClaude: React.FC = () => {
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const req = params.get('req') || '';
  const ret = params.get('return') || '';
  const fallback = params.get('fallback') || '';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!localStorage.getItem('auth_token')) {
      // Sign in, then come straight back to this approval — never a dashboard.
      const back = window.location.pathname + window.location.search;
      navigate(`/login?redirect=${encodeURIComponent(back)}`);
    }
  }, [navigate]);

  if (!req || !ret) {
    return (
      <div style={styles.wrap}>
        <div style={styles.card}>
          <h1 style={styles.h1}>Nothing to connect</h1>
          <p style={styles.p}>Start the connection from Claude — this page finishes it.</p>
        </div>
      </div>
    );
  }

  const allow = async () => {
    setBusy(true);
    setError(null);
    try {
      // MUST go through the API base — a relative path lands on the frontend
      // host (Vercel), which answers POST with 405 (live 08-01).
      const res = await axios.post(`${getApiBaseUrl()}/plugin/claude-consent`, { req, return: ret });
      const url = res?.data?.url;
      if (!url) throw new Error('No return address');
      window.location.href = url;
    } catch (e: any) {
      setBusy(false);
      setError(e?.response?.data?.detail || 'Could not complete the connection. Please try again.');
    }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <div style={styles.badge}><ShieldCheck size={18} /></div>
        <h1 style={styles.h1}>Connect Claude to Agents Anywhere</h1>
        <p style={styles.p}>Claude is asking to work with your scoping tools.</p>
        <ul style={styles.list}>
          <li style={styles.li}>See and create <b>your</b> clients and problems</li>
          <li style={styles.li}>Scope problems — save details, read shared files</li>
          <li style={styles.li}>Finalize PRDs and route work on your behalf</li>
        </ul>
        {error && <p style={styles.err}>{error}</p>}
        <div style={styles.row}>
          <button style={styles.deny} onClick={() => window.history.back()} disabled={busy}>Cancel</button>
          <button style={styles.allow} onClick={allow} disabled={busy}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : 'Allow access'}
          </button>
        </div>
        <p style={styles.fine}>
          Claude only ever sees your own workspace, and you can disconnect at any time from Settings.
          {fallback && <> Have an Agents Anywhere builder account instead? <a href={fallback} style={styles.link}>Sign in there</a>.</>}
        </p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrap: { minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#eef3ff', padding: 20 },
  card: { background: '#fff', border: '1px solid #dfe4f0', borderRadius: 16, padding: 30, maxWidth: 430, width: '100%', boxShadow: '0 10px 40px rgba(17,22,36,.08)' },
  badge: { width: 34, height: 34, borderRadius: 10, background: '#eef3ff', color: '#4080f2', display: 'grid', placeItems: 'center', marginBottom: 14 },
  h1: { fontSize: 18, margin: '0 0 4px', letterSpacing: '-.01em', color: '#111624' },
  p: { fontSize: 13.5, color: '#4a5268', margin: '0 0 16px' },
  list: { listStyle: 'none', padding: 0, margin: '0 0 18px' },
  li: { background: '#eef3ff', borderRadius: 10, padding: '9px 13px', fontSize: 13, marginBottom: 6, color: '#111624' },
  row: { display: 'flex', gap: 10 },
  allow: { flex: 1, padding: 11, border: 0, borderRadius: 10, background: '#4080f2', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'grid', placeItems: 'center' },
  deny: { flex: 1, padding: 11, border: '1px solid #dfe4f0', borderRadius: 10, background: '#fff', color: '#4a5268', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  err: { color: '#d92d43', fontSize: 13, margin: '0 0 10px' },
  fine: { fontSize: 11.5, color: '#8a91a5', marginTop: 14 },
  link: { color: '#4080f2' },
};

export default ConnectClaude;
